import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../../layout/Layout";
import { useAuth } from "../../../components/context/AuthContext";
import {
  createLoadingControl,
  deleteLoadingControl,
  getShipmentCameraControlList,
} from "../../../api/sevkiyatCamera/SevkiyatCamApi";
import { downloadSelectedYuklemeKontrolAsZip } from "../../../utils/yuklemeKontrol/exportYuklemeKontrolExcel";
import AmericaReportModal from "./AmericaReportModal";

function getStatusLabel(status) {
  switch (status) {
    case "KANTAR_CAPTURED":
      return "Kantardan Geldi";
    case "FORM_IN_PROGRESS":
      return "Form Devam Ediyor";
    case "COMPLETED":
      return "Tamamlandı";
    case "CANCELLED":
      return "İptal";
    default:
      return status || "-";
  }
}

function getStatusStyle(status) {
  if (status === "COMPLETED") {
    return {
      background: "#f6ffed",
      color: "#237804",
      border: "1px solid #b7eb8f",
    };
  }

  if (status === "KANTAR_CAPTURED") {
    return {
      background: "#e6f4ff",
      color: "#0958d9",
      border: "1px solid #91caff",
    };
  }

  if (status === "FORM_IN_PROGRESS") {
    return {
      background: "#fffbe6",
      color: "#ad6800",
      border: "1px solid #ffe58f",
    };
  }

  if (status === "CANCELLED") {
    return {
      background: "#fff1f0",
      color: "#a8071a",
      border: "1px solid #ffccc7",
    };
  }

  return {
    background: "#f5f5f5",
    color: "#595959",
    border: "1px solid #d9d9d9",
  };
}

function getPayload(response) {
  return response?.data || response;
}

function getRecordOwnerName(record) {
  const createdBy = record?.createdBy;
  const populatedName =
    createdBy && typeof createdBy === "object"
      ? `${createdBy?.name || ""} ${createdBy?.surname || ""}`.trim() ||
        String(createdBy?.email || "").trim()
      : "";

  return (
    String(record?.createdByName || "").trim() ||
    populatedName ||
    String(record?.loadingControlForm?.generalSection?.controlUser || "").trim() ||
    "-"
  );
}

function YuklemeControlListPage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [records, setRecords] = useState([]);

  const [pagination, setPagination] = useState({
    page: 1,
    limit: 25,
    count: 0,
    totalPages: 1,
  });

  // Liste filtreleri
  const [tarih, setTarih] = useState("");
  const [konteynerNo, setKonteynerNo] = useState("");
  const [musteri, setMusteri] = useState("");

  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  // Toplu indirme seçimi (sayfa değişse de seçim kalıcı olsun diye id -> kayıt map'i)
  const [selectedMap, setSelectedMap] = useState({});
  const [downloading, setDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState("");
  const [americaReportOpen, setAmericaReportOpen] = useState(false);

  // Yeni kayıt alanları
  const [newFormVisible, setNewFormVisible] = useState(false);
  const [newKonteynerNo, setNewKonteynerNo] = useState("");
  const [newFirmaAdi, setNewFirmaAdi] = useState("");
  const [newNote, setNewNote] = useState("");

  const fetchRecords = useCallback(
    async (nextPage = 1) => {
      try {
        setLoading(true);
        setErrorMessage("");

        const res = await getShipmentCameraControlList({
          page: nextPage,
          limit: pagination.limit,
          tarih: tarih || undefined,
          konteynerNo: konteynerNo.trim() || undefined,
          firmaAdi: musteri.trim() || undefined,
        });

        const payload = getPayload(res);

        if (!payload?.success) {
          throw new Error(payload?.message || "Liste getirilemedi.");
        }

        setRecords(payload?.data?.records || []);

        setPagination(
          payload?.data?.pagination || {
            page: nextPage,
            limit: pagination.limit,
            count: 0,
            totalPages: 1,
          }
        );
      } catch (error) {
        setErrorMessage(
          error?.response?.data?.message ||
            error?.message ||
            "Sevkiyat kayıtları getirilirken hata oluştu."
        );
      } finally {
        setLoading(false);
      }
    },
    [pagination.limit, tarih, konteynerNo, musteri]
  );

  useEffect(() => {
    if (!user) return;

    fetchRecords(1);

    // İlk yüklemede çalışsın; filtreler butonla tetiklensin.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    fetchRecords(1);
  };

  const handleCreateLoadingControl = async (e) => {
    e.preventDefault();

    const cleanKonteynerNo = newKonteynerNo.trim().toUpperCase();
    const cleanFirmaAdi = newFirmaAdi.trim();

    if (!cleanKonteynerNo) {
      setErrorMessage("Konteyner numarası zorunludur.");
      return;
    }

    if (!cleanFirmaAdi) {
      setErrorMessage("Firma adı zorunludur.");
      return;
    }

    try {
      setCreating(true);
      setErrorMessage("");

      const res = await createLoadingControl({
        konteynerNo: cleanKonteynerNo,
        firmaAdi: cleanFirmaAdi,
        note: newNote.trim(),
      });

      const payload = getPayload(res);

      if (!payload?.success) {
        throw new Error(payload?.message || "Yeni kayıt oluşturulamadı.");
      }

      const createdId = payload?.data?._id;

      setNewKonteynerNo("");
      setNewFirmaAdi("");
      setNewNote("");
      setNewFormVisible(false);

      if (createdId) {
        navigate(`/sevkiyat/yukleme-kontrol/${createdId}`);
        return;
      }

      fetchRecords(1);
    } catch (error) {
      setErrorMessage(
        error?.response?.data?.message ||
          error?.message ||
          "Yeni sevkiyat kaydı oluşturulurken hata oluştu."
      );
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteRecord = async (recordId) => {
    const confirmed = window.confirm(
      "Bu form pasife alınacak. Devam etmek istiyor musun?"
    );

    if (!confirmed) return;

    try {
      setLoading(true);
      setErrorMessage("");

      const res = await deleteLoadingControl({ id: recordId });
      const payload = getPayload(res);

      if (!payload?.success) {
        throw new Error(payload?.message || "Kayıt pasife alınamadı.");
      }

      setRecords((prev) => prev.filter((item) => item._id !== recordId));
      setPagination((prev) => ({
        ...prev,
        count: Math.max((prev.count || 1) - 1, 0),
      }));
    } catch (error) {
      setErrorMessage(
        error?.response?.data?.message ||
          error?.message ||
          "Kayıt pasife alınırken hata oluştu."
      );
    } finally {
      setLoading(false);
    }
  };

  const toggleRecordSelection = (record) => {
    setSelectedMap((prev) => {
      const next = { ...prev };

      if (next[record._id]) {
        delete next[record._id];
      } else {
        next[record._id] = record;
      }

      return next;
    });
  };

  const selectedIds = Object.keys(selectedMap);
  // Object anahtar sırası eklenme (seçim) sırasıyla aynıdır: operatör hangi
  // sırayla işaretlerse konteynerler raporda/fotoğraf havuzunda o sırada gelir.
  const selectedRecordsOrdered = Object.values(selectedMap);
  const allVisibleSelected =
    records.length > 0 && records.every((record) => selectedMap[record._id]);

  const toggleSelectAllVisible = () => {
    setSelectedMap((prev) => {
      const next = { ...prev };

      if (allVisibleSelected) {
        records.forEach((record) => delete next[record._id]);
      } else {
        records.forEach((record) => {
          next[record._id] = record;
        });
      }

      return next;
    });
  };

  const handleDownloadSelected = async () => {
    const selectedRecords = selectedRecordsOrdered;

    if (selectedRecords.length === 0) return;

    try {
      setDownloading(true);
      setErrorMessage("");

      await downloadSelectedYuklemeKontrolAsZip(selectedRecords, (info) => {
        if (info.asama === "excel") {
          setDownloadProgress(
            `Kayıt ${info.kayitIndex}/${info.kayitSayisi} - Excel hazırlanıyor...`
          );
        } else if (info.asama === "photo") {
          setDownloadProgress(
            `Kayıt ${info.kayitIndex}/${info.kayitSayisi} - Fotoğraf ${info.fotoIndex}/${info.fotoSayisi} indiriliyor...`
          );
        } else if (info.asama === "zip") {
          setDownloadProgress("Zip dosyası oluşturuluyor...");
        }
      });

      setSelectedMap({});
    } catch (error) {
      setErrorMessage(
        error?.message || "Seçili kayıtlar indirilirken hata oluştu."
      );
    } finally {
      setDownloading(false);
      setDownloadProgress("");
    }
  };

  if (!user) return null;

  return (
    <Layout defaultCollapsed>
      <style>{`
        .yukleme-list-page {
          flex: 1;
          width: 100%;
          max-width: 100%;
          min-width: 0;
          min-height: 420px;
          padding: 32px;
          background: #ffffff;
          border-radius: 18px;
          box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
          box-sizing: border-box;
        }

        .yukleme-page-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 16px;
          margin-bottom: 24px;
        }

        .yukleme-page-title-wrapper {
          min-width: 0;
        }

        .yukleme-page-title {
          margin: 0 0 8px;
          color: #283049;
          font-size: 26px;
          font-weight: 600;
          line-height: 1.25;
        }

        .yukleme-page-description {
          max-width: 720px;
          margin: 0;
          color: #6c757d;
          font-size: 15px;
          line-height: 1.6;
        }

        .yukleme-new-button {
          flex-shrink: 0;
        }

        .yukleme-create-form {
          margin-bottom: 22px;
          padding: 18px;
          background: #fafafa;
          border: 1px solid #f0f0f0;
          border-radius: 14px;
        }

        .yukleme-create-grid {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 14px;
        }

        .yukleme-create-actions {
          display: flex;
          justify-content: flex-end;
          gap: 10px;
          margin-top: 16px;
        }

        .yukleme-search-form {
          display: grid;
          grid-template-columns:
            minmax(180px, 0.8fr)
            minmax(220px, 1.2fr)
            minmax(240px, 1.5fr)
            auto;
          align-items: end;
          gap: 12px;
          margin-bottom: 20px;
        }

        .yukleme-form-group {
          min-width: 0;
        }

        .yukleme-table-wrapper {
          width: 100%;
          max-width: 100%;
          overflow-x: auto;
          border: 1px solid #f0f0f0;
          border-radius: 14px;
          -webkit-overflow-scrolling: touch;
        }

        .yukleme-table {
          width: 100%;
          min-width: 950px;
          border-collapse: collapse;
        }

        .yukleme-table tbody tr {
          transition: background 0.2s ease;
        }

        .yukleme-table tbody tr:hover {
          background: #fafcff;
        }

        .yukleme-note-cell {
          max-width: 280px;
          white-space: normal;
          overflow-wrap: anywhere;
        }

        .yukleme-action-cell {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
        }

        .yukleme-pagination {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 16px;
          margin-top: 16px;
          color: #6c757d;
          font-size: 14px;
        }

        .yukleme-pagination-actions {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .yukleme-pagination-page {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-width: 68px;
          padding: 0 8px;
          white-space: nowrap;
        }

        @media (max-width: 1100px) {
          .yukleme-list-page {
            padding: 24px;
          }

          .yukleme-search-form {
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }

          .yukleme-search-button {
            width: 100%;
          }
        }

        @media (max-width: 768px) {
          .yukleme-list-page {
            min-height: auto;
            padding: 18px 14px;
            border-radius: 14px;
          }

          .yukleme-page-header {
            flex-direction: column;
            align-items: stretch;
            gap: 18px;
            margin-bottom: 20px;
          }

          .yukleme-page-title {
            font-size: 22px;
          }

          .yukleme-page-description {
            font-size: 14px;
            line-height: 1.55;
          }

          .yukleme-new-button {
            width: 100%;
            min-height: 44px;
          }

          .yukleme-create-form {
            padding: 15px;
            border-radius: 12px;
          }

          .yukleme-create-grid,
          .yukleme-search-form {
            grid-template-columns: minmax(0, 1fr);
          }

          .yukleme-create-actions {
            flex-direction: column-reverse;
          }

          .yukleme-create-actions button,
          .yukleme-search-button {
            width: 100%;
            min-height: 44px;
          }

          .yukleme-table-wrapper {
            overflow: visible;
            border: none;
            border-radius: 0;
          }

          .yukleme-table {
            min-width: 0;
            border-collapse: separate;
            border-spacing: 0 12px;
          }

          .yukleme-table thead {
            display: none;
          }

          .yukleme-table tbody {
            display: block;
          }

          .yukleme-table tbody tr {
            display: block;
            overflow: hidden;
            background: #ffffff;
            border: 1px solid #e8e8e8;
            border-radius: 14px;
            box-shadow: 0 3px 12px rgba(40, 48, 73, 0.06);
          }

          .yukleme-table tbody tr:hover {
            background: #ffffff;
          }

          .yukleme-table tbody tr.yukleme-empty-row {
            box-shadow: none;
          }

          .yukleme-table tbody td {
            display: grid;
            grid-template-columns: minmax(105px, 38%) minmax(0, 1fr);
            align-items: center;
            gap: 12px;
            width: 100%;
            min-height: 48px;
            padding: 11px 14px !important;
            border-bottom: 1px solid #f1f1f1 !important;
            box-sizing: border-box;
            overflow-wrap: anywhere;
          }

          .yukleme-table tbody td:last-child {
            border-bottom: none !important;
          }

          .yukleme-table tbody td::before {
            content: attr(data-label);
            color: #8c8c8c;
            font-size: 12px;
            font-weight: 700;
            line-height: 1.4;
          }

          .yukleme-table tbody td.yukleme-empty-cell {
            display: block;
            padding: 24px 14px !important;
            text-align: center;
          }

          .yukleme-table tbody td.yukleme-empty-cell::before {
            display: none;
          }

          .yukleme-note-cell {
            max-width: none;
          }

          .yukleme-action-button {
            width: 100%;
            min-height: 42px;
          }

          .yukleme-action-cell {
            justify-content: flex-start;
          }

          .yukleme-pagination {
            flex-direction: column;
            align-items: stretch;
            gap: 12px;
            margin-top: 18px;
          }

          .yukleme-pagination-total {
            text-align: center;
          }

          .yukleme-pagination-actions {
            display: grid;
            grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
            width: 100%;
          }

          .yukleme-pagination-actions button {
            width: 100%;
          }
        }

        @media (max-width: 420px) {
          .yukleme-list-page {
            padding: 16px 10px;
          }

          .yukleme-table tbody td {
            grid-template-columns: 96px minmax(0, 1fr);
            padding: 10px 12px !important;
          }

          .yukleme-pagination-actions {
            grid-template-columns:
              minmax(0, 1fr)
              minmax(52px, auto)
              minmax(0, 1fr);
            gap: 6px;
          }
        }
      `}</style>

      <section className="yukleme-list-page">
        <div className="yukleme-page-header">
          <div className="yukleme-page-title-wrapper">
            <h2 className="yukleme-page-title">Yükleme Kontrol Listesi</h2>

            <p className="yukleme-page-description">
              Konteyner yükleme kontrol kayıtlarını görüntüleyebilir, kontrol
              formunu doldurabilir veya yeni bir kayıt oluşturabilirsiniz.
            </p>
          </div>

          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <button
              type="button"
              className="yukleme-new-button"
              onClick={handleDownloadSelected}
              disabled={selectedIds.length === 0 || downloading}
              style={{
                ...secondaryButtonStyle,
                padding: "11px 16px",
                opacity: selectedIds.length === 0 || downloading ? 0.6 : 1,
                cursor:
                  selectedIds.length === 0 || downloading
                    ? "not-allowed"
                    : "pointer",
              }}
            >
              {downloading
                ? downloadProgress || "İndiriliyor..."
                : `Seçilenleri İndir (${selectedIds.length})`}
            </button>

            <button
              type="button"
              className="yukleme-new-button"
              onClick={() => setAmericaReportOpen(true)}
              disabled={selectedIds.length === 0}
              style={{
                ...secondaryButtonStyle,
                padding: "11px 16px",
                opacity: selectedIds.length === 0 ? 0.6 : 1,
                cursor: selectedIds.length === 0 ? "not-allowed" : "pointer",
              }}
            >
              {`Amerika Raporu Oluştur (${selectedIds.length})`}
            </button>

            <button
              type="button"
              className="yukleme-new-button"
              onClick={() => setNewFormVisible((prev) => !prev)}
              style={{
                ...primaryButtonStyle,
                padding: "11px 16px",
              }}
            >
              {newFormVisible ? "Formu Kapat" : "Yeni Kayıt Oluştur"}
            </button>
          </div>
        </div>

        {errorMessage && (
          <div
            style={{
              marginBottom: 18,
              padding: "12px 14px",
              background: "#fff1f0",
              color: "#a8071a",
              border: "1px solid #ffccc7",
              borderRadius: 10,
              fontSize: 14,
              lineHeight: 1.5,
              overflowWrap: "anywhere",
            }}
          >
            {errorMessage}
          </div>
        )}

        {newFormVisible && (
          <form
            className="yukleme-create-form"
            onSubmit={handleCreateLoadingControl}
          >
            <h3
              style={{
                marginTop: 0,
                marginBottom: 16,
                color: "#283049",
                fontSize: 18,
                fontWeight: 600,
              }}
            >
              Yeni Sevkiyat Kontrol Kaydı
            </h3>

            <div className="yukleme-create-grid">
              <div className="yukleme-form-group">
                <label style={labelStyle}>Konteyner No</label>

                <input
                  value={newKonteynerNo}
                  onChange={(e) =>
                    setNewKonteynerNo(e.target.value.toUpperCase())
                  }
                  disabled={creating}
                  placeholder="Örn. SELU4690646"
                  autoComplete="off"
                  style={{
                    ...inputStyle,
                    textTransform: "uppercase",
                  }}
                />
              </div>

              <div className="yukleme-form-group">
                <label style={labelStyle}>Firma Adı</label>

                <input
                  value={newFirmaAdi}
                  onChange={(e) => setNewFirmaAdi(e.target.value)}
                  disabled={creating}
                  placeholder="Firma adı"
                  autoComplete="off"
                  style={inputStyle}
                />
              </div>

              <div className="yukleme-form-group">
                <label style={labelStyle}>Not</label>

                <input
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  disabled={creating}
                  placeholder="Opsiyonel not"
                  style={inputStyle}
                />
              </div>
            </div>

            <div className="yukleme-create-actions">
              <button
                type="button"
                onClick={() => setNewFormVisible(false)}
                disabled={creating}
                style={{
                  ...secondaryButtonStyle,
                  opacity: creating ? 0.6 : 1,
                  cursor: creating ? "not-allowed" : "pointer",
                }}
              >
                Vazgeç
              </button>

              <button
                type="submit"
                disabled={
                  creating || !newKonteynerNo.trim() || !newFirmaAdi.trim()
                }
                style={{
                  ...primaryButtonStyle,
                  background:
                    creating || !newKonteynerNo.trim() || !newFirmaAdi.trim()
                      ? "#bfbfbf"
                      : "#1677ff",
                  cursor:
                    creating || !newKonteynerNo.trim() || !newFirmaAdi.trim()
                      ? "not-allowed"
                      : "pointer",
                }}
              >
                {creating ? "Oluşturuluyor..." : "Kaydı Aç"}
              </button>
            </div>
          </form>
        )}

        <form className="yukleme-search-form" onSubmit={handleSearchSubmit}>
          <div className="yukleme-form-group">
            <label style={labelStyle}>Tarih</label>

            <input
              type="date"
              value={tarih}
              onChange={(e) => setTarih(e.target.value)}
              style={inputStyle}
            />
          </div>

          <div className="yukleme-form-group">
            <label style={labelStyle}>Konteyner</label>

            <input
              value={konteynerNo}
              onChange={(e) => setKonteynerNo(e.target.value.toUpperCase())}
              placeholder="Konteyner no ara"
              autoComplete="off"
              style={{
                ...inputStyle,
                textTransform: "uppercase",
              }}
            />
          </div>

          <div className="yukleme-form-group">
            <label style={labelStyle}>Müşteri</label>

            <input
              value={musteri}
              onChange={(e) => setMusteri(e.target.value)}
              placeholder="Müşteri / firma adı ara"
              autoComplete="off"
              style={inputStyle}
            />
          </div>

          <button
            type="submit"
            className="yukleme-search-button"
            disabled={loading}
            style={{
              ...primaryButtonStyle,
              height: 44,
              background: loading ? "#bfbfbf" : "#1677ff",
              cursor: loading ? "not-allowed" : "pointer",
            }}
          >
            {loading ? "Aranıyor..." : "Ara"}
          </button>
        </form>

        <div className="yukleme-table-wrapper">
          <table className="yukleme-table">
            <thead>
              <tr style={{ background: "#fafafa" }}>
                <th style={{ ...thStyle, width: 40 }}>
                  <input
                    type="checkbox"
                    checked={allVisibleSelected}
                    onChange={toggleSelectAllVisible}
                    style={{ width: 16, height: 16, cursor: "pointer" }}
                  />
                </th>
                <th style={thStyle}>Konteyner No</th>
                <th style={thStyle}>Müşteri</th>
                <th style={thStyle}>Not</th>
                <th style={thStyle}>Kayıt Sahibi</th>
                <th style={thStyle}>Durum</th>
                <th style={thStyle}>Tarih</th>
                <th style={thStyle}>İşlem</th>
              </tr>
            </thead>

            <tbody>
              {records.length === 0 && (
                <tr className="yukleme-empty-row">
                  <td
                    colSpan={8}
                    className="yukleme-empty-cell"
                    style={{
                      padding: 24,
                      textAlign: "center",
                      color: "#8c8c8c",
                      fontSize: 14,
                    }}
                  >
                    {loading ? "Kayıtlar yükleniyor..." : "Kayıt bulunamadı."}
                  </td>
                </tr>
              )}

              {records.map((record) => {
                const formHeader = record?.loadingControlForm?.formHeader || {};

                const recordKonteynerNo =
                  record?.konteynerNo || formHeader?.konteynerNo || "-";

                const recordFirmaAdi =
                  record?.firmaAdi || formHeader?.musteri || "-";

                return (
                  <tr key={record._id}>
                    <td data-label="Seç" style={tdStyle}>
                      <input
                        type="checkbox"
                        checked={!!selectedMap[record._id]}
                        onChange={() => toggleRecordSelection(record)}
                        style={{ width: 16, height: 16, cursor: "pointer" }}
                      />
                    </td>

                    <td data-label="Konteyner No" style={tdStyle}>
                      <strong>{recordKonteynerNo}</strong>
                    </td>

                    <td data-label="Müşteri" style={tdStyle}>
                      {recordFirmaAdi}
                    </td>

                    <td
                      data-label="Not"
                      className="yukleme-note-cell"
                      style={tdStyle}
                    >
                      {record?.note || "-"}
                    </td>

                    <td data-label="Kayıt Sahibi" style={tdStyle}>
                      {getRecordOwnerName(record)}
                    </td>

                    <td data-label="Durum" style={tdStyle}>
                      <span
                        style={{
                          ...getStatusStyle(record.status),
                          display: "inline-flex",
                          padding: "5px 9px",
                          borderRadius: 999,
                          fontSize: 12,
                          fontWeight: 600,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {getStatusLabel(record.status)}
                      </span>
                    </td>

                    <td data-label="Tarih" style={tdStyle}>
                      {record.createdAt
                        ? new Date(record.createdAt).toLocaleString("tr-TR")
                        : "-"}
                    </td>

                    <td data-label="İşlem" style={tdStyle}>
                      <div className="yukleme-action-cell">
                        <button
                          type="button"
                          className="yukleme-action-button"
                          onClick={() =>
                            navigate(`/sevkiyat/yukleme-kontrol/${record._id}`)
                          }
                          style={secondaryButtonStyle}
                        >
                          Detay / Form
                        </button>
                        <button
                          type="button"
                          className="yukleme-action-button"
                          onClick={() => handleDeleteRecord(record._id)}
                          style={{
                            ...secondaryButtonStyle,
                            borderColor: "#ffccc7",
                            color: "#cf1322",
                          }}
                        >
                          Pasife Al
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="yukleme-pagination">
          <span className="yukleme-pagination-total">
            Toplam kayıt: {pagination.count}
          </span>

          <div className="yukleme-pagination-actions">
            <button
              type="button"
              disabled={pagination.page <= 1 || loading}
              onClick={() => fetchRecords(pagination.page - 1)}
              style={{
                ...secondaryButtonStyle,
                opacity: pagination.page <= 1 || loading ? 0.55 : 1,
                cursor:
                  pagination.page <= 1 || loading ? "not-allowed" : "pointer",
              }}
            >
              Önceki
            </button>

            <span className="yukleme-pagination-page">
              {pagination.page} / {pagination.totalPages || 1}
            </span>

            <button
              type="button"
              disabled={pagination.page >= pagination.totalPages || loading}
              onClick={() => fetchRecords(pagination.page + 1)}
              style={{
                ...secondaryButtonStyle,
                opacity:
                  pagination.page >= pagination.totalPages || loading
                    ? 0.55
                    : 1,
                cursor:
                  pagination.page >= pagination.totalPages || loading
                    ? "not-allowed"
                    : "pointer",
              }}
            >
              Sonraki
            </button>
          </div>
        </div>
      </section>

      <AmericaReportModal
        open={americaReportOpen}
        onClose={() => setAmericaReportOpen(false)}
        records={selectedRecordsOrdered}
      />
    </Layout>
  );
}

const labelStyle = {
  display: "block",
  marginBottom: 7,
  color: "#283049",
  fontSize: 13,
  fontWeight: 600,
};

const inputStyle = {
  width: "100%",
  height: 44,
  padding: "0 12px",
  background: "#fff",
  border: "1px solid #d9d9d9",
  borderRadius: 10,
  outline: "none",
  boxSizing: "border-box",
  fontSize: 14,
};

const primaryButtonStyle = {
  padding: "10px 15px",
  background: "#1677ff",
  color: "#fff",
  border: "none",
  borderRadius: 10,
  fontWeight: 600,
  cursor: "pointer",
  whiteSpace: "nowrap",
};

const secondaryButtonStyle = {
  padding: "9px 13px",
  background: "#fff",
  color: "#283049",
  border: "1px solid #d9d9d9",
  borderRadius: 10,
  fontWeight: 600,
  cursor: "pointer",
  whiteSpace: "nowrap",
};

const thStyle = {
  padding: "12px 10px",
  color: "#595959",
  borderBottom: "1px solid #f0f0f0",
  textAlign: "left",
  fontSize: 13,
  fontWeight: 700,
  whiteSpace: "nowrap",
};

const tdStyle = {
  padding: "12px 10px",
  color: "#283049",
  borderBottom: "1px solid #f5f5f5",
  fontSize: 14,
  verticalAlign: "middle",
};

export default YuklemeControlListPage;
